import React, { useState, useEffect } from 'react';
import api from '../../services/api';
import { useDrop, useDrag } from 'react-dnd';
import '../styles/TeamGenerator.css';

const Player = ({ player, handleClick }) => {
  const [{ isDragging }, drag] = useDrag(() => ({
    type: 'PLAYER',
    item: player,
    collect: (monitor) => ({
      isDragging: monitor.isDragging(),
    }),
  }));

  return (
    <div
      ref={drag}
      className="player"
      style={{ opacity: isDragging ? 0.5 : 1 }}
      onClick={() => handleClick(player)}
    >
      <div>
        <p>{player.name}</p>
        <small>Skill: {player.skillLevel}</small>
      </div>
    </div>
  );
};

const Slot = ({ index, team, handleDropPlayer, handleSlotDoubleClick }) => {
  const [{ isOver }, drop] = useDrop(() => ({
    accept: 'PLAYER',
    drop: (item) => handleDropPlayer(index, item),
    collect: (monitor) => ({
      isOver: monitor.isOver(),
    }),
  }));

  return (
    <div
      ref={drop}
      className={`slot ${isOver ? 'highlight' : ''}`}
      style={{ backgroundColor: team[index] ? 'lightgray' : 'gray' }}
      onClick={() => handleSlotDoubleClick(index)}
    >
      {team[index] && <p>{team[index].name} (Skill: {team[index].skillLevel})</p>}
    </div>
  );
};

const TeamGenerator = () => {
  const [players, setPlayers] = useState([]);
  const [team, setTeam] = useState(Array(10).fill(null));
  const [selectedPlayers, setSelectedPlayers] = useState([]);
  const [teamA, setTeamA] = useState(Array(5).fill(null));
  const [teamB, setTeamB] = useState(Array(5).fill(null));
  const [teamASkill, setTeamASkill] = useState(0);
  const [teamBSkill, setTeamBSkill] = useState(0);
  const [swapSelection, setSwapSelection] = useState([]);
  const [searchTerm, setSearchTerm] = useState("");


  useEffect(() => {
  api.get('/api/players/public')
    .then((response) => {
      setPlayers(sortPlayersByName(response.data));
    })
    .catch((error) => {
      console.error('Error fetching players:', error);
    });
  }, []);

  const sortPlayersByName = (players) => {
    return [...players].sort((a, b) =>
      a.name.localeCompare(b.name)
    );
  };

  const handleDropPlayer = (index, player) => {
    setTeam((prevTeam) => {
      if (
        !prevTeam[index] &&
        !prevTeam.some((p) => p?.id === player.id) &&
        prevTeam.includes(null)
      ) {
        const updatedTeam = [...prevTeam];
        updatedTeam[index] = player;
        return updatedTeam;
      }
      return prevTeam;
    });

    setSelectedPlayers((prevSelected) => {
      if (!prevSelected.some((p) => p.id === player.id) && team.includes(null)) {
        return [...prevSelected, player];
      }
      return prevSelected;
    });
  };

  const handlePlayerDoubleClick = (player) => {
    setTeam((prevTeam) => {
      const firstEmptyIndex = prevTeam.findIndex((p) => p === null);
      if (firstEmptyIndex !== -1 && !prevTeam.some((p) => p?.id === player.id)) {
        const updatedTeam = [...prevTeam];
        updatedTeam[firstEmptyIndex] = player;
        return updatedTeam;
      }
      return prevTeam;
    });

    setSelectedPlayers((prevSelected) => {
      if (!prevSelected.some((p) => p.id === player.id) && team.includes(null)) {
        return [...prevSelected, player];
      }
      return prevSelected;
    });
  };

  const handleSlotDoubleClick = (index) => {
    setTeam((prevTeam) => {
      const updatedTeam = [...prevTeam];
      const removedPlayer = updatedTeam[index];
      updatedTeam[index] = null;
      setSelectedPlayers((prevSelected) =>
        prevSelected.filter((p) => p.id !== removedPlayer?.id)
      );
      return updatedTeam;
    });
  };

  const generateTeams = async () => {
  if (selectedPlayers.length !== 10) {
    alert("You must select exactly 10 players.");
    return;
  }

  try {
    const response = await api.post("/api/team/generate", {
      playerIds: selectedPlayers.map(p => p.id),
    });

    const data = response.data;

    setTeamA(data.teamA);
    setTeamB(data.teamB);
    setTeamASkill(data.teamASkill);
    setTeamBSkill(data.teamBSkill);
  } catch (error) {
    console.error("Error generating teams:", error);
    alert("Failed to generate balanced teams. Check server logs.");
  }
};


  const handleSwapClick = (player) => {
    if (!player) return;
    setSwapSelection((prev) => {
      const alreadySelected = prev.find((p) => p.id === player.id);
      if (alreadySelected) {
        return prev.filter((p) => p.id !== player.id);
      } else if (prev.length < 2) {
        return [...prev, player];
      } else {
        return [player];
      }
    });
  };

  const handleSwapPlayers = () => {
    const [player1, player2] = swapSelection;
    const newTeamA = teamA.map((p) =>
      p?.id === player1.id ? player2 : p?.id === player2.id ? player1 : p
    );
    const newTeamB = teamB.map((p) =>
      p?.id === player1.id ? player2 : p?.id === player2.id ? player1 : p
    );
    const newTeamASkill = newTeamA.reduce((sum, p) => sum + (p?.skillLevel || 0), 0);
    const newTeamBSkill = newTeamB.reduce((sum, p) => sum + (p?.skillLevel || 0), 0);

    setTeamA(newTeamA);
    setTeamB(newTeamB);
    setTeamASkill(newTeamASkill);
    setTeamBSkill(newTeamBSkill);
    setSwapSelection([]);
  };

  const totalSkillScore = selectedPlayers.reduce(
    (total, player) => total + (player.skillLevel || 0),
    0
  );

  return (
    <div className="team-generator">
      <div className="left-section">
       <h3 className="text-center fw-bold mb-3">Player Pool</h3>
        <input
          type="text"
          placeholder="Search players..."
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          className="player-search"
        />
        <div className="players">
          {players
            .filter((player) =>
              player.name.toLowerCase().includes(searchTerm.toLowerCase())
            )
            .filter((player) => !selectedPlayers.some((p) => p.id === player.id))
            .map((player) => (
              <Player
                key={player.id}
                player={player}
                handleClick={handlePlayerDoubleClick}
              />
            ))}
        </div>
      </div>

      <div className="middle-section">
        <h3  className="text-center fw-bold mb-3">Team Slots</h3>
        <div className="total-skill-score">
          Total Group Score: {totalSkillScore}
        </div>
        <div className="slots-container">
          {team.map((_, index) => (
            <Slot
              key={index}
              index={index}
              team={team}
              handleDropPlayer={handleDropPlayer}
              handleSlotDoubleClick={handleSlotDoubleClick}
            />
          ))}
        </div>
        <button className="btn btn-primary me-2" onClick={generateTeams}>
  Generate Teams
</button>

{swapSelection.length === 2 && (
  <button className="btn btn-secondary" onClick={handleSwapPlayers}>
    Swap Selected Players
  </button>
)}
      </div>

      <div className="right-section">
        <div className="team-container team-a-container">
          <h3  className="text-center fw-bold mb-3">Team A (Skill: {teamASkill})</h3>
          <div className="slot-column">
            {teamA.map((player, index) => (
              <div
                key={index}
                className="team-a-slot"
                onClick={() => handleSwapClick(player)}
                style={{
                  border: swapSelection.includes(player) ? '2px solid blue' : 'none',
                  cursor: 'pointer',
                }}
              >
                {player ? player.name : ''}
              </div>
            ))}
          </div>
        </div>
        <div className="team-container team-b-container">
          <h3  className="text-center fw-bold mb-3">Team B (Skill: {teamBSkill})</h3>
          <div className="slot-column">
            {teamB.map((player, index) => (
              <div
                key={index}
                className="team-b-slot"
                onClick={() => handleSwapClick(player)}
                style={{
                  border: swapSelection.includes(player) ? '2px solid blue' : 'none',
                  cursor: 'pointer',
                }}
              >
                {player ? player.name : ''} 
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
};

export default TeamGenerator;
